/**
 * MessageWidgets — Generic widget registry and renderer for agent messages.
 *
 * Agent messages can include structured widget data in `metadata.widgets`.
 * Each widget has a `type` (e.g. "suggestions") and a `data` payload.
 * This component maps each type to a React renderer via WIDGET_REGISTRY.
 *
 * To add a new widget type:
 *   1. Create a renderer component (e.g. ChartWidget.tsx)
 *   2. Add it to WIDGET_REGISTRY below
 *   3. Add LLM prompt instructions for the ```widget:<type>``` format
 *   No other changes needed — extraction and plumbing are generic.
 */
import React from 'react';
import { Box } from '@mui/material';
import ActionSuggestions from './ActionSuggestions';
import type { SuggestedAction } from './ActionSuggestions';

// ── Types ────────────────────────────────────────────────────────────

export interface MessageWidget {
  type: string;
  data: unknown;
}

export interface MessageWidgetsProps {
  widgets: MessageWidget[];
  onSuggestionClick?: (prompt: string, label?: string) => void;
  disabled?: boolean;
}

// ── Suggestion renderer adapter ──────────────────────────────────────

const SuggestionsRenderer: React.FC<{
  data: unknown;
  onSuggestionClick?: (prompt: string, label?: string) => void;
  disabled?: boolean;
}> = ({ data, onSuggestionClick, disabled }) => {
  if (!Array.isArray(data) || !onSuggestionClick) return null;
  return (
    <ActionSuggestions
      actions={data as SuggestedAction[]}
      onActionClick={onSuggestionClick}
      disabled={disabled}
    />
  );
};

// ── Widget registry ──────────────────────────────────────────────────
// Map widget type string → React component.
// Each renderer receives { data, disabled, ...handler props }.

const WIDGET_REGISTRY: Record<
  string,
  React.FC<{ data: any; disabled?: boolean; onSuggestionClick?: (prompt: string) => void }>
> = {
  suggestions: SuggestionsRenderer,
  // Future widgets:
  // chart: ChartWidget,
  // confirmation: ConfirmationWidget,
};

// ── Main component ───────────────────────────────────────────────────

const MessageWidgets: React.FC<MessageWidgetsProps> = ({ widgets, disabled, onSuggestionClick }) => {
  if (!widgets?.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {widgets.map((w, i) => {
        const Renderer = WIDGET_REGISTRY[w.type];
        if (!Renderer) return null;
        return (
          <Renderer
            key={`${w.type}-${i}`}
            data={w.data}
            disabled={disabled}
            onSuggestionClick={onSuggestionClick}
          />
        );
      })}
    </Box>
  );
};

export default MessageWidgets;
